import React, { ComponentType, useMemo } from "react";

import { Pie } from "react-chartjs-2";
import {
  ArcElement,
  Chart as ChartJS,
  Legend,
  Tooltip,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

import ExportToXlsxButtonJs from "../../../CommonComponents/ExportToXlsxButton/ExportToXlsxButton";
import RebetLoaderJs from "../../../shared/RebetLoader/RebetLoader";
import { useGetFinancialsDepositsByPlatform } from "../../../../queries/financials/useGetFinancialsDepositsByPlatform";
import type { FinancialsDepositPlatformSlice } from "../../../../queries/financials/useGetFinancialsDepositsByPlatform";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { formatCurrency } from "../../../../utils/financialFormatters";
import { useCurrencySymbol } from "../../../../hooks/useCurrencySymbol";

import styles from "./DepositByPlatform.module.css";

ChartJS.register(ArcElement, Tooltip, Legend);

const ExportToXlsxButton = ExportToXlsxButtonJs as ComponentType<any>;
const RebetLoader = RebetLoaderJs as ComponentType<any>;

interface DepositByPlatformProps {
  startDate: string;
  endDate: string;
  isApiRunning?: boolean;
}

/** Uses API percentage when present (including 0); derives only when omitted. */
const slicePercent = (
  slice: FinancialsDepositPlatformSlice,
  total: number,
): number => {
  if (slice.percentage != null) return Math.round(slice.percentage);
  if (slice.depositAmount <= 0 || total <= 0) return 0;
  return Math.round((slice.depositAmount / total) * 100);
};

const DepositByPlatform: React.FC<DepositByPlatformProps> = ({
  startDate,
  endDate,
  isApiRunning = false,
}) => {
  const currencySymbol = useCurrencySymbol() ?? "$";

  const dateFilters = useMemo(
    () => ({ start_date: startDate, end_date: endDate }),
    [startDate, endDate],
  );

  const {
    data: platforms = [],
    isFetching,
    isError,
  } = useGetFinancialsDepositsByPlatform(dateFilters);

  const total = useMemo(
    () => platforms.reduce((sum, p) => sum + p.depositAmount, 0),
    [platforms],
  );

  const visiblePlatforms = useMemo(
    () => platforms.filter((p) => p.depositAmount > 0),
    [platforms],
  );

  const chartData = useMemo(
    () => ({
      labels: visiblePlatforms.map((p) => p.name),
      datasets: [
        {
          data: visiblePlatforms.map((p) => p.depositAmount),
          backgroundColor: visiblePlatforms.map((p) => p.color),
          borderColor: "var(--grey-800)",
          borderWidth: 2,
        },
      ],
    }),
    [visiblePlatforms],
  );

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: true,
          backgroundColor: "#252729",
          padding: 12,
          displayColors: true,
          titleFont: { family: "Poppins-regular", size: 13, weight: 600 },
          bodyFont: { family: "Poppins-regular", size: 13 },
          callbacks: {
            title: (items: { label: string }[]) => items?.[0]?.label ?? "",
            label: (item: { raw: unknown; dataIndex: number }) => {
              const slice = visiblePlatforms[item.dataIndex];
              const value = Number(item.raw);
              const pct = slice ? slicePercent(slice, total) : 0;
              const formatted = formatCurrency(value, currencySymbol);
              return `${formatted} (${pct}%)`;
            },
          },
        },
        datalabels: {
          color: "#ffffff",
          font: { family: "Poppins-regular", size: 12, weight: 600 },
          textAlign: "center" as const,
          formatter: (value: number, ctx: { dataIndex: number }) => {
            const slice = visiblePlatforms[ctx.dataIndex];
            const pct = slice ? slicePercent(slice, total) : 0;
            const formatted = formatCurrency(value, currencySymbol);
            return `${formatted}\n(${pct}%)`;
          },
        },
      },
    }),
    [currencySymbol, total, visiblePlatforms],
  );

  const exportRows = useMemo(
    () =>
      platforms.map((p) => ({
        platform_id: p.platformId,
        name: p.name,
        deposit_amount: p.depositAmount,
        percentage: slicePercent(p, total),
      })),
    [platforms, total],
  );

  const showLoading = isApiRunning || isFetching;
  const hasDepositData = visiblePlatforms.length > 0;

  return (
    <section className={styles.card} aria-label="Deposit by Platform">
      <header className={styles.header}>
        <h2 className={styles.title}>Deposit by Platform</h2>
        <div className={styles.controls}>
          <ExportToXlsxButton
            data={exportRows}
            filename="deposits-by-platform.xlsx"
          />
        </div>
      </header>

      <div className={styles.chartContainer}>
        {showLoading ? (
          <div className={styles.loaderOverlay} data-testid="chart-loader">
            <RebetLoader width={100} height={100} />
          </div>
        ) : isError ? (
          <p
            className={styles.errorBanner}
            role="alert"
            data-testid="deposits-by-platform-error"
          >
            {AppStrings.FINANCIALS_DEPOSITS_BY_PLATFORM_LOAD_ERROR}
          </p>
        ) : !hasDepositData ? (
          <p
            className={styles.emptyMessage}
            data-testid="deposits-by-platform-empty"
          >
            {AppStrings.FINANCIALS_DEPOSITS_BY_PLATFORM_EMPTY_STATE}
          </p>
        ) : (
          <Pie
            data={chartData}
            options={chartOptions as any}
            plugins={[ChartDataLabels as any]}
          />
        )}
      </div>

      {platforms.length > 0 ? (
        <ul className={styles.legend}>
          {platforms.map((p) => (
            <li key={p.platformId} className={styles.legendItem}>
              <span
                className={styles.legendDot}
                style={{ backgroundColor: p.color }}
                aria-hidden="true"
              />
              {p.name}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
};

export default DepositByPlatform;
